import { useEffect } from 'react'

/**
 * useCanvasTriggers — desktop input bindings for the Doc ↔ Canvas
 * morph. Three triggers per ArkturianClaude's IACP 97a49444:
 *
 *   1. Pinch-trackpad (macOS, Windows precision-touchpad). The
 *      browser delivers this as a `wheel` event with `ctrlKey: true`
 *      and the deltaY indicates pinch direction. deltaY < 0 = pinch-out
 *      (open canvas), deltaY > 0 = pinch-in (close canvas).
 *
 *   2. Cmd+Scroll (or Ctrl+Scroll on non-Mac). Same `wheel` event
 *      with metaKey or ctrlKey but without trackpad-pinch. We
 *      distinguish by detecting whether the wheel event came from a
 *      precise pinch (deltaY ≤ ~5) vs a scroll-wheel notch (≥ 30).
 *      Both treat as morph triggers; the wheel-direction maps the same
 *      way (up = open, down = close).
 *
 *   3. Discovery button — wired separately at the toggle UI. This
 *      hook handles only the global input listeners.
 *
 * Calls `onOpen` or `onClose` once per trigger event. Debounced — a
 * fast burst of wheel events triggers only the first.
 */

interface Options {
  onOpen: () => void
  onClose: () => void
  /** Enabled flag — when false the hook does not attach listeners. */
  enabled?: boolean
  /** Ignore wheel events while pointer is inside an element matching
   *  this selector (e.g. inside the active TipTap editor, so doc-mode
   *  scrolling isn't hijacked). */
  ignoreSelector?: string
}

export function useCanvasTriggers({
  onOpen,
  onClose,
  enabled = true,
  ignoreSelector,
}: Options) {
  useEffect(() => {
    if (!enabled) return

    let lastFireTs = 0
    const DEBOUNCE_MS = 600 // longer than the open-animation so we don't double-fire

    const handleWheel = (e: WheelEvent) => {
      // Only react to ctrl/meta-modified wheel events (intentional
      // gestures); plain scroll passes through to the document.
      if (!e.ctrlKey && !e.metaKey) return

      // Ignore if pointer is inside an "ignore" zone (e.g. the live
      // TipTap editor in doc-mode — its own scroll behavior wins).
      if (ignoreSelector) {
        const target = e.target as Element | null
        if (target?.closest(ignoreSelector)) return
      }

      e.preventDefault()
      const now = performance.now()
      if (now - lastFireTs < DEBOUNCE_MS) return
      lastFireTs = now

      if (e.deltaY < 0) onOpen()
      else if (e.deltaY > 0) onClose()
    }

    // Capture-phase + non-passive so we can preventDefault on
    // Cmd+Scroll (which the browser would otherwise interpret as
    // page-zoom).
    window.addEventListener('wheel', handleWheel, {
      capture: true,
      passive: false,
    })
    return () => {
      window.removeEventListener('wheel', handleWheel, { capture: true })
    }
  }, [onOpen, onClose, enabled, ignoreSelector])
}
